import React, { useMemo, useState, memo } from "react";
import { Box, Typography, Button } from "@mui/material";
import { convertArrayToString, getFromCms, openGoogleMap, removingSeconds } from "../../../../helpers/common";
import { formatTimeRange } from "../../../../utils/formatTime";
import { format } from "date-fns";
import { IRequest, iEventDetailCMS, iEventType } from "../../../../types/eventTypes";
import { iDetailListItemProps } from "../../../../types/contentType";
import { getBudgetValueToDisplay } from "../../../../pages/Events/utill";
import IsMobile from "../../../../hooks/isMobile";
import CalendarBlank from "../../../../assets/svg-fc/CalendarBlank";
import CompleteMenuModal from "../../complete-menu-modal/CompleteMenuModal";
import OccasionSvg from "../../../../assets/svg-fc/OccasionSvg";
import ClockSvg from "../../../../assets/svg-fc/ClockSvg";
import MoneyIcon from "../../../../assets/svg-fc/MoneyIcon";
import DetailListItem from "../../../reusable-components/details-list-item/DetailListItem";
import EventOtherFieldValue, {
	getCmsPresetDisplayValue,
} from "../../../reusable-components/event-other-field-value/EventOtherFieldValue";
import UserBlank from "../../../../assets/svg-fc/UserBlank";
import MapPinSvg from "../../../../assets/svg-fc/MapPinSvg";
import { getPrepaidMenuDisplayText, getPrepaidMenuSelectionPackages } from "../../../../helpers/prepaidMenuHelpers";
import PrepaidMenuSelections from "../../prepaid-menu-selections/PrepaidMenuSelections";
import "./b2cDetailsTabContent.scss";

interface DetailsTabContentProps {
	selectedType: string;
	eventDetail: iEventType;
	request?: IRequest;
	cms: iEventDetailCMS;
}

const B2CDetailsTabContent: React.FC<DetailsTabContentProps> = ({ eventDetail, request, cms }) => {
	const isMobile = IsMobile();
	const [completeMenuModalState, setCompleteMenuModalState] = useState(false);
	const isRequest = !!request;
	const menuDisplayText = useMemo(() => getPrepaidMenuDisplayText(request), [request]);
	const menuSelectionPackages = useMemo(() => getPrepaidMenuSelectionPackages(request), [request]);
	const menuPreviewContent = menuSelectionPackages?.length ? (
		<PrepaidMenuSelections packages={menuSelectionPackages} previewMaxLength={100} />
	) : (
		menuDisplayText.substring(0, 100)
	);

	const detailTabContentItem = (): iDetailListItemProps[] => {
		const otherCuisineText = eventDetail?.otherKindOfFoods?.value?.trim() || "";
		const otherCoursesText = eventDetail?.otherCourses?.value?.trim() || "";
		const cuisinePresets = getCmsPresetDisplayValue(
			(eventDetail?.typeOfFoods || []).concat(eventDetail?.typeOfCuisines || []),
			cms,
		);
		const coursesPresets = getCmsPresetDisplayValue(eventDetail?.courses, cms);

		const items = [
			{
				title: "Event Date",
				icon: <CalendarBlank />,
				value: eventDetail.eventDate && format(new Date(eventDetail.eventDate), "dd MMM yyyy"),
			},
			{
				title: "No. of Guests",
				icon: <UserBlank />,
				value: isRequest ? eventDetail?.request?.guestCount : eventDetail?.guestsCount,
				detailListItemClass: "guestCount",
			},
			{
				title: "Budget",
				icon: <MoneyIcon />,
				value: getBudgetValueToDisplay(eventDetail),
				flexible: eventDetail?.isBudgetFlexible,
				optionalDesc: "",
			},
			!isRequest && {
				title: "Event type",
				value: getFromCms(eventDetail?.serviceCategory?.value, cms),
			},
			!isRequest && {
				title: "Occasion",
				icon: <OccasionSvg />,
				value:
					eventDetail?.serviceEventType?.value === "OTHER" && eventDetail?.otherServiceEventType?.value
						? eventDetail?.otherServiceEventType.value
						: getFromCms(eventDetail?.serviceEventType?.value, cms),
			},
			{
				title: "Event time",
				icon: <ClockSvg />,
				value: isRequest
					? formatTimeRange(removingSeconds(eventDetail?.startTime), removingSeconds(eventDetail?.endTime), " - ")
					: getFromCms(eventDetail?.timeOfDay?.value, cms),
			},
			!isRequest && {
				title: "Provider type",
				value: getFromCms(eventDetail?.providerType?.value, cms),
			},
			!isRequest && {
				title: "Service type",
				value: eventDetail?.providerOptions?.value
					? getFromCms(eventDetail?.providerOptions?.value, cms)
					: convertArrayToString(eventDetail?.providerOptionsMultiple, cms),
			},
			!isRequest && {
				title: "Cuisine",
				detailListItemClass: "hasOtherField",
				renderValue: () => (
					<EventOtherFieldValue
						presetValue={cuisinePresets}
						otherText={otherCuisineText}
						otherLabel="Other cuisine"
					/>
				),
			},
			isRequest &&
				menuDisplayText && {
					title: "Menu Items",
					value: (
						<>
							<Box>
								{menuSelectionPackages?.length ? (
									menuPreviewContent
								) : (
									<Typography variant="inherit" sx={{ whiteSpace: "pre-wrap" }}>
										{menuPreviewContent}
									</Typography>
								)}
								<Button
									variant="text"
									onClick={() => setCompleteMenuModalState(true)}
									sx={{
										fontSize: 16,
										fontWeight: 500,
										textDecoration: "underline",
										textTransform: "unset",
										justifyContent: "flex-start",
										"@media (min-width: 1023.98px)": {
											fontSize: 18,
										},
									}}
								>
									see complete menu
								</Button>
							</Box>
						</>
					),
				},
			!isRequest && {
				title: "Courses",
				detailListItemClass: "hasOtherField",
				renderValue: () => (
					<EventOtherFieldValue
						presetValue={coursesPresets}
						otherText={otherCoursesText}
						otherLabel="Other courses"
					/>
				),
			},
			!isRequest &&
				Boolean(eventDetail?.dishType) && {
					title: "Special Instructions",
					value: eventDetail?.dishType,
				},
			!!eventDetail?.address?.place_name && {
				title: "Venue",
				icon: <MapPinSvg />,
				value:
					[
						eventDetail?.address?.place_name || "",
						eventDetail?.address?.addressLine2 || "",
						getFromCms(eventDetail?.city + "", cms) || "",
					]
						.filter((str) => !!str)
						.join(", ")
						.trim() || "None",
				onClick: () => {
					if (eventDetail?.address?.place_name) {
						openGoogleMap(eventDetail?.address);
					}
				},
				detailListItemClass: `icon_remove ${eventDetail?.address?.place_name ? "venue" : ""}`,
			},
			{
				title: "City",
				value: getFromCms(eventDetail?.city + "", cms),
			},
			isRequest &&
				eventDetail?.request?.payload?.note && {
					title: "Customer notes",
					value: eventDetail?.request?.payload?.note ?? "",
				},
		];

		return items.filter((item) => {
			return (
				!!item &&
				typeof item === "object" &&
				"title" in item &&
				(Boolean("renderValue" in item && item.renderValue) ||
					(item.value !== undefined && item.value !== null && item.value !== ""))
			);
		}) as iDetailListItemProps[];
	};

	return (
		<>
			<Box className="detailsTabContentB2C">
				{isMobile && (
					<>
						<DetailListItem {...detailTabContentItem()[0]} />
						<DetailListItem {...detailTabContentItem()[1]} />
					</>
				)}
				<Box className="detailsGrid">
					{(isMobile ? detailTabContentItem().slice(2) : detailTabContentItem()).map((item, index) => (
						<DetailListItem key={index} {...item} />
					))}
				</Box>
				{/* <B2CEventDetailListItem title={paymentTerms?.title} value={paymentTerms?.value} /> */}
			</Box>

			{isRequest && !!menuDisplayText && (
				<CompleteMenuModal
					menu={
						menuSelectionPackages?.length ? (
							<PrepaidMenuSelections packages={menuSelectionPackages} />
						) : (
							menuDisplayText
						)
					}
					open={completeMenuModalState}
					onClose={() => setCompleteMenuModalState(false)}
				/>
			)}
		</>
	);
};

export default memo(B2CDetailsTabContent);
